import type { BoundaryItemsAction } from '~/store/boundaryItems/boundaryItems.action';
import type { BoundaryTerritoryGroupsAction } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.action';
import { activeBTGIdPerBoundaryGroupChanged } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import { BOUNDARY_TERRITORY_GROUP_REMOVE_COMPLETED } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionTypes';
import type { ActiveMapElementsAction } from '~/store/frontendState/activeMapElements/activeMapElements.action';
import {
  activeMapElementsSetPlace,
  clearSpreadsheetPreviewFilterState,
  deactivateActiveBoundary, deactivateActiveProximity, proximityHovered, proximityHoverEnded,
  setActiveOverlappingElementsProximity, setActiveOverlappingLocation,
  setSpreadsheetPreviewFilterState,
} from '~/store/frontendState/activeMapElements/activeMapElements.actionCreators';
import type { DistanceCalculatorAction } from '~/store/frontendState/mapTools/distanceCalculator/distanceCalculator.action';
import type { DrawingEditAction } from '~/store/frontendState/mapTools/drawing/drawingEdit/drawingEdit.action';
import type { MapAction } from '~/store/map/map.action';
import { MAP_PAGE_INIT } from '~/store/map/map.actionTypes';
import type { MapSettingsProximityAction } from '~/store/mapSettings/proximity/mapSettingsProximity.action';
import type { MapSettingsToolsStateBoundaryStateAction } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.action';
import type { LocationFinderToolsStateAction } from '~/store/mapSettings/toolsState/locationFinder/locationFinder.action';
import { setLocationFinderToolsState } from '~/store/mapSettings/toolsState/locationFinder/locationFinder.actionCreators';
import type { SpreadsheetDataAction } from '~/store/spreadsheetData/spreadsheetData.action';
import { FRONTEND_STATE_DRAWING_EDIT_SET_SELECTED_DRAWING } from '../mapTools/drawing/drawingEdit/drawingEdit.actionTypes';
import {
  ACTIVE_MAP_ELEMENTS_CLEAR_STATE, ACTIVE_MAP_ELEMENTS_DEACTIVATE_ACTIVE_MARKER,
  ACTIVE_MAP_ELEMENTS_SET_ACTIVE_BOUNDARY, ACTIVE_MAP_ELEMENTS_SET_ACTIVE_BOUNDARY_TERRITORY,
  ACTIVE_MAP_ELEMENTS_SET_ACTIVE_MARKER_NAVIGATION_INDEX, ACTIVE_MAP_ELEMENTS_SET_ACTIVE_PROXIMITY,
  ACTIVE_MAP_SET_ACTIVE_LOCATION_LIST, ACTIVE_MAP_SET_ACTIVE_MASTER_KEY, SPREADSHEET_MARKER_CLICKED, SPREADSHEET_MARKER_HOVER_STOPPED,
  SPREADSHEET_MARKER_HOVERED,
} from './activeMapElements.actionTypes';
import {
  ActiveMapElementsConfiguration, type ActiveMapElementsState, type ActiveOverlappingMapElements,
} from './activeMapElements.state';

const overlappingInitialState: ActiveOverlappingMapElements = {
  activeOverlappingLocation: null,
  boundaries: [],
  proximities: [],
  configuration: null,
};

const spreadsheetPreviewFilterInitialState: ActiveMapElementsState['spreadsheetPreviewFilterState'] = {
  boundaries: [],
  boundaryTerritories: [],
  proximities: [],
};

export const initialState: ActiveMapElementsState = {
  activeMarker: null,
  activeMarkerNavigationIndex: 0,
  activeBoundary: null,
  activeBoundaryTerritory: null,
  activeOverlappingElements: overlappingInitialState,
  activeProximity: null,
  activePlace: null,
  hoveredProximity: null,
  hoveredMarker: null,
  isLocationListActive: false,
  isMasterKeyActive: false,
  spreadsheetPreviewFilterState: spreadsheetPreviewFilterInitialState,
};

export const activeMapElementsReducer = (
  state = initialState, action: ActiveMapElementsAction | MapSettingsProximityAction
  | BoundaryTerritoryGroupsAction| BoundaryItemsAction | DrawingEditAction |
    LocationFinderToolsStateAction | DistanceCalculatorAction | MapAction | SpreadsheetDataAction | MapSettingsToolsStateBoundaryStateAction
): ActiveMapElementsState => {
  if (setLocationFinderToolsState.match(action)) {
    return initialState;
  }
  if (deactivateActiveProximity.match(action)) {
    return {
      ...state,
      activeProximity: initialState.activeProximity,
      activeOverlappingElements: initialState.activeOverlappingElements,
    };
  }
  if (deactivateActiveBoundary.match(action)) {
    return {
      ...state,
      activeBoundary: initialState.activeBoundary,
      activeBoundaryTerritory: initialState.activeBoundaryTerritory,
      activeOverlappingElements: initialState.activeOverlappingElements,
    };
  }
  if (setActiveOverlappingElementsProximity.match(action)) {
    return {
      ...state,
      activeOverlappingElements: {
        ...state.activeOverlappingElements,
        boundaries: initialState.activeOverlappingElements.boundaries,
        proximities: action.payload.elements,
        configuration: ActiveMapElementsConfiguration.Proximity,
      },
    };
  }

  if (setActiveOverlappingLocation.match(action)) {
    return {
      ...state,
      activeOverlappingElements: {
        ...state.activeOverlappingElements,
        activeOverlappingLocation: action.payload.loc,
      },
    };
  }

  if (proximityHovered.match(action)) {
    return {
      ...state,
      hoveredProximity: action.payload,
    };
  }
  if (proximityHoverEnded.match(action)) {
    if (action.payload.proximityId === state.hoveredProximity?.proximityId) {
      return {
        ...state,
        hoveredProximity: null,
      };
    }
  }
  if (activeBTGIdPerBoundaryGroupChanged.match(action)) {
    const { boundaryGroupId, activeBTGId } = action.payload;
    if (state.activeBoundary?.boundaryGroupId === boundaryGroupId) {
      return {
        ...state,
        activeBoundary: {
          ...state.activeBoundary,
          boundaryTerritoryGroupId: activeBTGId,
        },
      };
    }
    if (state.activeBoundaryTerritory) {
      return {
        ...state,
        activeBoundaryTerritory: null,
      };
    }
    return state;
  }

  if (activeMapElementsSetPlace.match(action)) {
    return {
      ...initialState,
      activePlace: action.payload.place,
    };
  }

  if (setSpreadsheetPreviewFilterState.match(action)) {
    return {
      ...state,
      spreadsheetPreviewFilterState: {
        ...spreadsheetPreviewFilterInitialState,
        ...action.payload.newFilters,
      },
    };
  }

  if (clearSpreadsheetPreviewFilterState.match(action)) {
    return {
      ...state,
      spreadsheetPreviewFilterState: spreadsheetPreviewFilterInitialState,
    };
  }

  switch (action.type) {
    case SPREADSHEET_MARKER_CLICKED:
      return {
        ...initialState,
        isLocationListActive: state.isLocationListActive,
        isMasterKeyActive: state.isMasterKeyActive,
        activeMarker: action.payload,
        activeMarkerNavigationIndex: initialState.activeMarkerNavigationIndex,
      };

    case SPREADSHEET_MARKER_HOVERED:
      return {
        ...state,
        hoveredMarker: action.payload,
      };

    case MAP_PAGE_INIT:
    case SPREADSHEET_MARKER_HOVER_STOPPED:
      return {
        ...state,
        hoveredMarker: null,
      };

    case ACTIVE_MAP_ELEMENTS_SET_ACTIVE_MARKER_NAVIGATION_INDEX:
      return {
        ...state,
        activeMarkerNavigationIndex: action.payload.index,
      };

    case ACTIVE_MAP_ELEMENTS_SET_ACTIVE_BOUNDARY: {
      return {
        ...initialState,
        isLocationListActive: state.isLocationListActive,
        isMasterKeyActive: state.isMasterKeyActive,
        activeBoundary: action.payload,
      };
    }

    case ACTIVE_MAP_ELEMENTS_SET_ACTIVE_BOUNDARY_TERRITORY: {
      return {
        ...initialState,
        isLocationListActive: state.isLocationListActive,
        isMasterKeyActive: state.isMasterKeyActive,
        activeBoundaryTerritory: action.payload,
      };
    }

    case BOUNDARY_TERRITORY_GROUP_REMOVE_COMPLETED: {
      const { boundaryTerritoryGroups: removedBTGs } = action.payload;

      return {
        ...state,
        ...(removedBTGs.some(btg => btg.boundaryGroupId === state.activeBoundary?.boundaryGroupId)
          ? { activeBoundary: initialState.activeBoundary } : {}),
        ...(removedBTGs.some(btg => btg.boundaryTerritoryGroupId === state.activeBoundary?.boundaryTerritoryGroupId)
          ? { activeBoundaryTerritory: initialState.activeBoundaryTerritory } : {}),
      };
    }

    case FRONTEND_STATE_DRAWING_EDIT_SET_SELECTED_DRAWING:
    case ACTIVE_MAP_ELEMENTS_CLEAR_STATE: {
      return initialState;
    }

    case ACTIVE_MAP_ELEMENTS_DEACTIVATE_ACTIVE_MARKER: {
      return {
        ...state,
        activeMarker: initialState.activeMarker,
        activeMarkerNavigationIndex: initialState.activeMarkerNavigationIndex,
      };
    }

    case ACTIVE_MAP_ELEMENTS_SET_ACTIVE_PROXIMITY:
      return {
        ...initialState,
        activeOverlappingElements: state.activeOverlappingElements,
        activeProximity: action.payload,
        isLocationListActive: state.isLocationListActive,
        isMasterKeyActive: state.isMasterKeyActive,
      };

    case ACTIVE_MAP_SET_ACTIVE_LOCATION_LIST:
      return {
        ...initialState,
        isLocationListActive: true,
      };

    case ACTIVE_MAP_SET_ACTIVE_MASTER_KEY:
      return {
        ...initialState,
        isMasterKeyActive: true,
      };

    default:
      return state;
  }
};
